// Generated by GitHub Copilot

import { useCallback } from 'react';
import { useMsal, useIsAuthenticated, useAccount } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { loginRequest, apiRequest } from '../auth/authConfig';

/**
 * useAuth — central authentication hook for the GCC Studio app.
 *
 * Returns:
 * - `isAuthenticated` {boolean} — whether the user is signed in.
 * - `isLoading` {boolean} — true while MSAL is initialising or in a redirect.
 * - `account` {AccountInfo|null} — the signed-in account object.
 * - `login()` — triggers the loginRedirect flow.
 * - `logout()` — triggers the logoutRedirect flow.
 * - `getApiToken()` — silently acquires an access token for the back-end API,
 *   falling back to an interactive redirect if the silent call fails
 *   (e.g. consent required or token expired).
 *
 * Security:
 * - Tokens are never stored in component state; they are requested on demand
 *   from the MSAL cache (sessionStorage) each time `getApiToken` is called.
 * - InteractionRequiredAuthError is the only case where we fall back to
 *   interactive login — all other errors are re-thrown for the caller to handle.
 */
export function useAuth() {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null);

  const isLoading =
    inProgress === InteractionStatus.Startup ||
    inProgress === InteractionStatus.HandleRedirect ||
    inProgress === InteractionStatus.Login;

  /**
   * Initiates the Microsoft redirect login flow.
   */
  const login = useCallback(() => {
    instance.loginRedirect(loginRequest).catch((err) => {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[useAuth] login error:', err);
      }
    });
  }, [instance]);

  /**
   * Signs the user out and redirects to the app origin.
   */
  const logout = useCallback(() => {
    instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
      account: instance.getActiveAccount(),
    }).catch((err) => {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[useAuth] logout error:', err);
      }
    });
  }, [instance]);

  /**
   * Acquires an access token for calling the configured back-end API.
   * Attempts a silent (cached) token first; falls back to an interactive
   * redirect if consent is required or the refresh token has expired.
   *
   * @returns {Promise<string>} The raw access token string.
   * @throws {Error} If no account is available or if the token acquisition fails
   *   for a reason other than InteractionRequired.
   */
  const getApiToken = useCallback(async () => {
    const activeAccount = instance.getActiveAccount();
    if (!activeAccount) {
      throw new Error('[useAuth] No active account — user must sign in first.');
    }

    try {
      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: activeAccount,
      });
      return response.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Silent acquisition failed — user interaction needed (MFA, consent, etc.)
        instance.acquireTokenRedirect({
          ...apiRequest,
          account: activeAccount,
        });
        // acquireTokenRedirect navigates away; throw to prevent further execution
        throw err;
      }
      throw err;
    }
  }, [instance]);

  return { isAuthenticated, isLoading, account, login, logout, getApiToken };
}
